import React, { useState, useEffect, useMemo, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../context/FormContext';
import { useNavigate } from 'react-router-dom';
import type { ApplicantRole, FormData } from '../types/form';
import FormStepLayout from '../components/FormStepLayout';
import FieldError from '../components/FieldError';
import { PhoneIcon, EmailIcon, SmartphoneIcon } from '../components/Icons';
import {
  applicationStepRequiredFieldsFilled,
  validateApplicationStepFields,
  type ApplicationStepFieldKey,
} from '../utils/fieldValidation';
import '../styles/theme.css';
import '../styles/form.css';
import '../styles/form-step.css';

type FocusedField =
  | 'first_name'
  | 'last_name'
  | 'phone_number'
  | 'email'
  | 'date_of_birth'
  | 'incident_date'
  | 'reporter_first_name'
  | 'reporter_last_name'
  | 'reporter_phone_number'
  | 'reporter_email'
  | null;

const ApplicationForm = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    formData,
    updateFormData,
    ensureDraft,
    persistDraft,
    saveError,
    isSaving,
    applicationGuid,
    draftReady,
  } = useForm();
  const [applicant_role, setApplicantRole] = useState<ApplicantRole>(
    formData.applicant_role ?? 'patient'
  );
  const [first_name, setFirstName] = useState(formData.first_name ?? '');
  const [last_name, setLastName] = useState(formData.last_name ?? '');
  const [phone_number, setPhoneNumber] = useState(formData.phone_number ?? '');
  const [email, setEmail] = useState(formData.email ?? '');
  const [reporter_first_name, setReporterFirstName] = useState(formData.reporter_first_name ?? '');
  const [reporter_last_name, setReporterLastName] = useState(formData.reporter_last_name ?? '');
  const [reporter_phone_number, setReporterPhoneNumber] = useState(
    formData.reporter_phone_number ?? ''
  );
  const [reporter_email, setReporterEmail] = useState(formData.reporter_email ?? '');
  const [contact_preference, setContactPreference] = useState(
    formData.contact_preference || 'call'
  );
  const [date_of_birth, setDateOfBirth] = useState(formData.date_of_birth ?? '');
  const [incident_date, setIncidentDate] = useState(formData.incident_date ?? '');
  const [focusedField, setFocusedField] = useState<FocusedField>(null);

  const navigate = useNavigate();
  const isPatient = applicant_role === 'patient';

  const fieldErrors = useMemo(
    () =>
      validateApplicationStepFields({
        isPatient,
        first_name,
        last_name,
        phone_number,
        email,
        reporter_first_name,
        reporter_last_name,
        reporter_phone_number,
        reporter_email,
        date_of_birth,
        incident_date,
      }),
    [
      isPatient,
      first_name,
      last_name,
      phone_number,
      email,
      reporter_first_name,
      reporter_last_name,
      reporter_phone_number,
      reporter_email,
      date_of_birth,
      incident_date,
    ]
  );

  const fe = (key: ApplicationStepFieldKey): string | undefined => fieldErrors[key];

  useEffect(() => {
    void ensureDraft();
  }, [ensureDraft]);

  /** After the server draft is loaded, copy snapshot into local field state. */
  useEffect(() => {
    if (!draftReady || !applicationGuid) return;
    setApplicantRole(formData.applicant_role ?? 'patient');
    setFirstName(formData.first_name ?? '');
    setLastName(formData.last_name ?? '');
    setPhoneNumber(formData.phone_number ?? '');
    setEmail(formData.email ?? '');
    setReporterFirstName(formData.reporter_first_name ?? '');
    setReporterLastName(formData.reporter_last_name ?? '');
    setReporterPhoneNumber(formData.reporter_phone_number ?? '');
    setReporterEmail(formData.reporter_email ?? '');
    setContactPreference(formData.contact_preference || 'call');
    setDateOfBirth(formData.date_of_birth ?? '');
    setIncidentDate(formData.incident_date ?? '');
    // Intentionally omit formData from deps: hydrate once when the draft becomes ready, not on every PATCH.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftReady, applicationGuid]);

  const applicationStepReady =
    applicationStepRequiredFieldsFilled({
      isPatient,
      first_name,
      last_name,
      phone_number,
      email,
      reporter_first_name,
      reporter_last_name,
      reporter_phone_number,
      reporter_email,
      date_of_birth,
      incident_date,
    }) && Object.keys(fieldErrors).length === 0;

  const fieldClass = (name: FocusedField, value: string, error?: string): string =>
    `form-field ${value ? 'has-value' : ''} ${focusedField === name ? 'focused' : ''} ${error ? 'form-field--error' : ''}`.trim();

  const phoneFieldClass = (name: FocusedField, value: string, error?: string): string =>
    `form-field has-icon-left ${value ? 'has-value' : ''} ${focusedField === name ? 'focused' : ''} ${error ? 'form-field--error' : ''}`.trim();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!applicationStepReady) return;
    const next: FormData = {
      ...formData,
      applicant_role,
      first_name,
      last_name,
      phone_number,
      email,
      reporter_first_name: isPatient ? '' : reporter_first_name,
      reporter_last_name: isPatient ? '' : reporter_last_name,
      reporter_phone_number: isPatient ? '' : reporter_phone_number,
      reporter_email: isPatient ? '' : reporter_email,
      contact_preference,
      date_of_birth,
      incident_date,
    };
    updateFormData(next);
    try {
      await persistDraft(next, 'incident');
      navigate('/incidents');
    } catch {
      /* saveError set in context */
    }
  };

  const renderContactAndDates = (): JSX.Element => (
    <>
      <span className="section-label">{t('forms.step1.contactPreference')}</span>
      <div className="segmented-group">
        <button
          type="button"
          className={`segmented-option ${contact_preference === 'call' ? 'active' : ''}`}
          onClick={() => setContactPreference('call')}
        >
          <PhoneIcon />
          {t('forms.step1.contactCall')}
        </button>
        <button
          type="button"
          className={`segmented-option ${contact_preference === 'text' ? 'active' : ''}`}
          onClick={() => setContactPreference('text')}
        >
          <SmartphoneIcon />
          {t('forms.step1.contactText')}
        </button>
        <button
          type="button"
          className={`segmented-option ${contact_preference === 'email' ? 'active' : ''}`}
          onClick={() => setContactPreference('email')}
        >
          <EmailIcon />
          {t('forms.step1.contactEmail')}
        </button>
      </div>

      <div
        className={`form-field form-field--date ${fe('date_of_birth') ? 'form-field--error' : ''}`}
      >
        <label className="form-label form-label--static" htmlFor="date_of_birth">
          {isPatient ? t('forms.step1.dobPatient') : t('forms.step1.dobOther')}
        </label>
        <input
          id="date_of_birth"
          type="date"
          className="form-input"
          required
          aria-required="true"
          value={date_of_birth}
          onChange={(e) => setDateOfBirth(e.target.value)}
        />
        <FieldError message={fe('date_of_birth')} />
      </div>

      <div
        className={`form-field form-field--date ${fe('incident_date') ? 'form-field--error' : ''}`}
      >
        <label className="form-label form-label--static" htmlFor="incident_date">
          {isPatient ? t('forms.step1.accidentDatePatient') : t('forms.step1.accidentDateOther')}
        </label>
        <input
          id="incident_date"
          type="date"
          className="form-input"
          required
          aria-required="true"
          value={incident_date}
          onChange={(e) => setIncidentDate(e.target.value)}
        />
        <FieldError message={fe('incident_date')} />
      </div>
    </>
  );

  return (
    <form onSubmit={handleSubmit}>
      {saveError && (
        <div role="alert" className="toast toast--error" style={{ marginBottom: '1rem' }}>
          {t(saveError)}
        </div>
      )}
      <FormStepLayout
        title={t('forms.step1.title')}
        currentStep={1}
        totalSteps={3}
        submitButton
        nextDisabled={!applicationStepReady || isSaving}
      >
        <span className="radio-group-label">{t('forms.step1.roleQuestion')}</span>
        <div className="radio-group">
          <label className="radio-option">
            <input
              type="radio"
              name="applicant_role"
              value="patient"
              checked={applicant_role === 'patient'}
              onChange={() => setApplicantRole('patient')}
            />
            {t('forms.step1.rolePatient')}
          </label>
          <label className="radio-option">
            <input
              type="radio"
              name="applicant_role"
              value="on_behalf"
              checked={applicant_role === 'on_behalf'}
              onChange={() => setApplicantRole('on_behalf')}
            />
            {t('forms.step1.roleOnBehalf')}
          </label>
        </div>

        {isPatient ? (
          <>
            <div className={fieldClass('first_name', first_name, fe('first_name'))}>
              <label className="form-label">{t('forms.step1.yourFirstName')}</label>
              <input
                type="text"
                className="form-input"
                value={first_name}
                onChange={(e) => setFirstName(e.target.value)}
                onFocus={() => setFocusedField('first_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="given-name"
              />
              <FieldError message={fe('first_name')} />
            </div>
            <div className={fieldClass('last_name', last_name, fe('last_name'))}>
              <label className="form-label">{t('forms.step1.yourLastName')}</label>
              <input
                type="text"
                className="form-input"
                value={last_name}
                onChange={(e) => setLastName(e.target.value)}
                onFocus={() => setFocusedField('last_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="family-name"
              />
              <FieldError message={fe('last_name')} />
            </div>
            <div className={phoneFieldClass('phone_number', phone_number, fe('phone_number'))}>
              <span className="input-icon input-icon-left" aria-hidden>
                <PhoneIcon />
              </span>
              <label className="form-label">{t('forms.step1.yourPhone')}</label>
              <input
                type="tel"
                className="form-input"
                value={phone_number}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onFocus={() => setFocusedField('phone_number')}
                onBlur={() => setFocusedField(null)}
                autoComplete="tel"
              />
              <FieldError message={fe('phone_number')} />
            </div>
            <div className={phoneFieldClass('email', email, fe('email'))}>
              <span className="input-icon input-icon-left" aria-hidden>
                <EmailIcon />
              </span>
              <label className="form-label">{t('forms.step1.yourEmail')}</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                autoComplete="email"
              />
              <FieldError message={fe('email')} />
            </div>
            {renderContactAndDates()}
          </>
        ) : (
          <>
            <h3 className="form-section-heading">{t('forms.step1.sectionYourInfo')}</h3>
            <div
              className={fieldClass('reporter_first_name', reporter_first_name, fe('reporter_first_name'))}
            >
              <label className="form-label">{t('forms.step1.yourFirstName')}</label>
              <input
                type="text"
                className="form-input"
                value={reporter_first_name}
                onChange={(e) => setReporterFirstName(e.target.value)}
                onFocus={() => setFocusedField('reporter_first_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="given-name"
              />
              <FieldError message={fe('reporter_first_name')} />
            </div>
            <div
              className={fieldClass('reporter_last_name', reporter_last_name, fe('reporter_last_name'))}
            >
              <label className="form-label">{t('forms.step1.yourLastName')}</label>
              <input
                type="text"
                className="form-input"
                value={reporter_last_name}
                onChange={(e) => setReporterLastName(e.target.value)}
                onFocus={() => setFocusedField('reporter_last_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="family-name"
              />
              <FieldError message={fe('reporter_last_name')} />
            </div>
            <div
              className={phoneFieldClass(
                'reporter_phone_number',
                reporter_phone_number,
                fe('reporter_phone_number')
              )}
            >
              <span className="input-icon input-icon-left" aria-hidden>
                <PhoneIcon />
              </span>
              <label className="form-label">{t('forms.step1.yourPhone')}</label>
              <input
                type="tel"
                className="form-input"
                value={reporter_phone_number}
                onChange={(e) => setReporterPhoneNumber(e.target.value)}
                onFocus={() => setFocusedField('reporter_phone_number')}
                onBlur={() => setFocusedField(null)}
                autoComplete="tel"
              />
              <FieldError message={fe('reporter_phone_number')} />
            </div>
            <div className={phoneFieldClass('reporter_email', reporter_email, fe('reporter_email'))}>
              <span className="input-icon input-icon-left" aria-hidden>
                <EmailIcon />
              </span>
              <label className="form-label">{t('forms.step1.yourEmail')}</label>
              <input
                type="email"
                className="form-input"
                value={reporter_email}
                onChange={(e) => setReporterEmail(e.target.value)}
                onFocus={() => setFocusedField('reporter_email')}
                onBlur={() => setFocusedField(null)}
                autoComplete="email"
              />
              <FieldError message={fe('reporter_email')} />
            </div>

            <h3 className="form-section-heading">{t('forms.step1.sectionPatientInfo')}</h3>
            <div className={fieldClass('first_name', first_name, fe('first_name'))}>
              <label className="form-label">{t('forms.step1.patientFirstName')}</label>
              <input
                type="text"
                className="form-input"
                value={first_name}
                onChange={(e) => setFirstName(e.target.value)}
                onFocus={() => setFocusedField('first_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="off"
              />
              <FieldError message={fe('first_name')} />
            </div>
            <div className={fieldClass('last_name', last_name, fe('last_name'))}>
              <label className="form-label">{t('forms.step1.patientLastName')}</label>
              <input
                type="text"
                className="form-input"
                value={last_name}
                onChange={(e) => setLastName(e.target.value)}
                onFocus={() => setFocusedField('last_name')}
                onBlur={() => setFocusedField(null)}
                autoComplete="off"
              />
              <FieldError message={fe('last_name')} />
            </div>
            <div className={phoneFieldClass('phone_number', phone_number, fe('phone_number'))}>
              <span className="input-icon input-icon-left" aria-hidden>
                <PhoneIcon />
              </span>
              <label className="form-label">{t('forms.step1.patientPhone')}</label>
              <input
                type="tel"
                className="form-input"
                value={phone_number}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onFocus={() => setFocusedField('phone_number')}
                onBlur={() => setFocusedField(null)}
                autoComplete="tel"
              />
              <FieldError message={fe('phone_number')} />
            </div>
            <div className={phoneFieldClass('email', email, fe('email'))}>
              <span className="input-icon input-icon-left" aria-hidden>
                <EmailIcon />
              </span>
              <label className="form-label">{t('forms.step1.patientEmail')}</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                autoComplete="email"
              />
              <FieldError message={fe('email')} />
            </div>
            {renderContactAndDates()}
          </>
        )}
      </FormStepLayout>
    </form>
  );
};

export default ApplicationForm;
